import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	Box,
	LinearProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { useState } from "react";
import {
	GasEntryDraft,
	GasEntryMeter,
	GasEntryTarget,
	GasEntryTargetZone,
	GasEntryTrackedShare,
} from "../models/GasEntry";
import {
	buildGasEntryDraft,
	type GasEntryDayRow,
	getEffectiveHeatValue,
	getMonthTotals,
	getSharePercents,
	getTotalMcf,
	getTrackedMcf,
	getTrackedMcfByTargetId,
	groupTrackedSharesByDate,
} from "../utils/gasEntryRows";
import { GasEntryMonthTotalRow } from "./GasEntryMonthTotalRow";
import { GasEntryRow } from "./GasEntryRow";

const HEAD_SX = {
	backgroundColor: "#fff",
	borderRight: "1px solid",
	borderColor: "divider",
	color: "text.secondary",
	fontSize: 11,
	fontWeight: 600,
	lineHeight: 1.25,
	px: 0.5,
	py: 0.25,
	textAlign: "center",
} as const;

const getColumnWidths = (meterCount: number, targetCount: number) => {
	const weights = [
		1.4, // date
		...Array.from({ length: meterCount * 3 }, () => 1), // meter: UV, pressure, temperature
		...Array.from({ length: 3 }, () => 1.1), // day total: heat value, Mcf, MMBtu
		...Array.from({ length: targetCount }, () => 1), // share per target
		...Array.from({ length: targetCount }, () => 1), // breakdown per target
		0.8, // actions
	];
	const total = weights.reduce((sum, weight) => sum + weight, 0);
	return weights.map((weight) => `${((weight / total) * 100).toFixed(3)}%`);
};

interface GasEntryTableProps {
	rows: GasEntryDayRow[];
	meters: GasEntryMeter[];
	targets: GasEntryTarget[];
	zoneShares: GasEntryTargetZone[];
	trackedShares: GasEntryTrackedShare[];
	isLoading: boolean;
	onSave: (draft: GasEntryDraft) => Promise<unknown>;
	onDelete: (gasEntryId: number) => void;
}

export const GasEntryTable = ({
	rows,
	meters,
	targets,
	zoneShares,
	trackedShares,
	isLoading,
	onSave,
	onDelete,
}: GasEntryTableProps) => {
	const [editingDate, setEditingDate] = useState<string | null>(null);
	const [draftRow, setDraftRow] = useState<GasEntryDayRow | null>(null);
	const [originalRow, setOriginalRow] = useState<GasEntryDayRow | null>(null);
	const [pendingRow, setPendingRow] = useState<GasEntryDayRow | null>(null);

	const zonesByTargetId: Record<number, GasEntryTargetZone[]> = {};
	for (const share of zoneShares)
		(zonesByTargetId[share.TargetId] ??= []).push(share);

	const sharesByDate = groupTrackedSharesByDate(trackedShares);
	const sharesOf = (date: string) => sharesByDate[date] ?? [];

	const withSharePercents = (row: GasEntryDayRow): GasEntryDayRow => ({
		...row,
		percentByTargetId: getSharePercents(
			targets,
			sharesOf(row.date),
			getTotalMcf(row, meters),
		),
	});

	const startEdit = (row: GasEntryDayRow) => {
		const editRow = { ...row, heatValueBtuPerCf: getEffectiveHeatValue(row) };

		setEditingDate(row.date);
		setDraftRow(editRow);
		setOriginalRow(editRow);
		setPendingRow(null);
	};

	const isDraftDirty =
		draftRow !== null &&
		originalRow !== null &&
		JSON.stringify(draftRow) !== JSON.stringify(originalRow);

	const requestEdit = (row: GasEntryDayRow) => {
		if (isDraftDirty) setPendingRow(row);
		else startEdit(row);
	};

	const visibleRows = rows.map((row) =>
		withSharePercents(
			editingDate === row.date && draftRow !== null ? draftRow : row,
		),
	);
	const monthTotals = getMonthTotals(
		visibleRows,
		meters,
		targets,
		sharesByDate,
	);

	const cancelEdit = () => {
		setEditingDate(null);
		setDraftRow(null);
	};

	const closeSaved = (date: string) => {
		setEditingDate((current) => (current === date ? null : current));
		setDraftRow((current) => (current?.date === date ? null : current));
	};

	const patchHeatValue = (value: number | null) =>
		setDraftRow((current) =>
			current === null ? current : { ...current, heatValueBtuPerCf: value },
		);

	const patchMeter = (
		gasMeterId: number,
		field: "uncorrectedVolume" | "pressurePsi" | "temperatureF",
		value: number | null,
	) =>
		setDraftRow((current) => {
			if (current === null) return current;
			const values = current.valuesByMeterId[gasMeterId] ?? {
				uncorrectedVolume: null,
				pressurePsi: null,
				temperatureF: null,
			};
			return {
				...current,
				valuesByMeterId: {
					...current.valuesByMeterId,
					[gasMeterId]: { ...values, [field]: value },
				},
			};
		});

	if (isLoading && rows.length === 0) {
		return (
			<Paper elevation={2} sx={{ flex: 1, minHeight: 0, p: 1 }}>
				<CenteredLazyLoading />
			</Paper>
		);
	}

	if (meters.length === 0) {
		return (
			<Paper elevation={2} sx={{ flex: 1, minHeight: 0, p: 4 }}>
				<Typography
					sx={{
						color: "text.secondary",
						fontSize: "0.85rem",
						textAlign: "center",
					}}
				>
					There are no gas meters configured.
				</Typography>
			</Paper>
		);
	}

	return (
		<Box sx={{ display: "flex", flex: 1, minHeight: 0, position: "relative" }}>
			{isLoading && rows.length > 0 && (
				<LinearProgress
					sx={{
						borderRadius: "4px 4px 0 0",
						left: 0,
						pointerEvents: "none",
						position: "absolute",
						right: 0,
						top: 0,
						zIndex: 3,
					}}
				/>
			)}
			<TableContainer
				component={Paper}
				elevation={2}
				sx={{ flex: 1, minHeight: 0, width: "100%" }}
			>
				<Table
					size="small"
					stickyHeader
					sx={{
						"& td": {
							borderRight: "1px solid",
							borderColor: "divider",
							boxSizing: "border-box",
							fontSize: 11,
							height: 28,
							lineHeight: 1.2,
							px: 0.5,
							py: 0,
						},
						"& .gas-entry-filler td": {
							border: 0,
							height: "auto",
							p: 0,
						},
						height: "100%",
						tableLayout: "fixed",
						width: "100%",
					}}
				>
					<colgroup>
						{getColumnWidths(meters.length, targets.length).map(
							(width, index) => (
								<col key={index} style={{ width }} />
							),
						)}
					</colgroup>
					<TableHead>
						<TableRow>
							<TableCell rowSpan={2} sx={HEAD_SX}>
								Date
							</TableCell>
							{meters.map((meter) => (
								<TableCell key={meter.GasMeterId} colSpan={3} sx={HEAD_SX}>
									{meter.MeterName}
								</TableCell>
							))}
							<TableCell colSpan={3} sx={HEAD_SX}>
								Day total
							</TableCell>
							{targets.length > 0 && (
								<>
									<TableCell colSpan={targets.length} sx={HEAD_SX}>
										Share (%)
									</TableCell>
									<TableCell colSpan={targets.length} sx={HEAD_SX}>
										Breakdown (MCF)
									</TableCell>
								</>
							)}
							<TableCell rowSpan={2} sx={HEAD_SX} />
						</TableRow>
						<TableRow>
							{meters.flatMap((meter) => [
								<TableCell key={`uv-${meter.GasMeterId}`} sx={HEAD_SX}>
									Meter (UV)
								</TableCell>,
								<TableCell key={`psi-${meter.GasMeterId}`} sx={HEAD_SX}>
									Pressure (psi)
								</TableCell>,
								<TableCell key={`temp-${meter.GasMeterId}`} sx={HEAD_SX}>
									Temp (°F)
								</TableCell>,
							])}
							<TableCell sx={HEAD_SX}>Heat value (BTU/cf)</TableCell>
							<TableCell sx={HEAD_SX}>Total MCF</TableCell>
							<TableCell sx={HEAD_SX}>Total MMBtu</TableCell>
							{targets.map((target) => (
								<TableCell key={`pct-${target.TargetId}`} sx={HEAD_SX}>
									{target.TargetName}
								</TableCell>
							))}
							{targets.map((target) => (
								<TableCell key={`brk-${target.TargetId}`} sx={HEAD_SX}>
									{target.TargetName}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{visibleRows.map((row) => {
							const isEditing = editingDate === row.date;

							return (
								<GasEntryRow
									key={row.date}
									row={row}
									meters={meters}
									targets={targets}
									zonesByTargetId={zonesByTargetId}
									trackedMcf={getTrackedMcf(sharesOf(row.date))}
									trackedMcfByTargetId={getTrackedMcfByTargetId(
										sharesOf(row.date),
									)}
									isEditing={isEditing}
									isLocked={editingDate !== null && !isEditing}
									isSaving={isLoading}
									onMeterPatch={patchMeter}
									onHeatValuePatch={patchHeatValue}
									onEdit={() => requestEdit(row)}
									onCancel={cancelEdit}
									onSave={() => {
										if (draftRow === null) return;
										onSave(buildGasEntryDraft(draftRow, meters)).then(
											() => closeSaved(row.date),
											() => {},
										);
									}}
									onDelete={() =>
										row.gasEntryId !== null && onDelete(row.gasEntryId)
									}
								/>
							);
						})}
						<TableRow
							aria-hidden
							className="gas-entry-filler"
							sx={{ height: "100%" }}
						>
							<TableCell colSpan={meters.length * 3 + targets.length * 2 + 5} />
						</TableRow>
					</TableBody>
					<GasEntryMonthTotalRow
						meters={meters}
						targets={targets}
						totals={monthTotals}
					/>
				</Table>
			</TableContainer>
			<ConfirmationDialogRaw
				title="Discard changes"
				message="This day has unsaved changes. Discard them?"
				open={pendingRow !== null}
				buttons={{ no: "Keep editing", yes: "Discard" }}
				onClose={(value) => {
					if (value === "OK" && pendingRow !== null) startEdit(pendingRow);
					else setPendingRow(null);
				}}
			/>
		</Box>
	);
};
